import { convertFromHTML } from "draft-convert";
import { ContentState, convertFromRaw, convertToRaw } from "draft-js";
import { List } from "immutable";
import { MediaDto, mediaFromDto } from "@domain/media/dtos";
import { PersonDto, personFromDto, personToDto } from "@domain/user/dtos";
import { Article, ArticleContent, ArticleDraft, ArticleSection, TextSection } from "./article";

interface ArticleContentDto extends Omit<ArticleContent, "sections" | "assets"> {
	sections: ArticleSectionDto[];
	assets: MediaDto[];
}

type ArticleSectionDto = Exclude<ArticleSection, TextSection> | TextSectionDto;

interface TextSectionDto extends Omit<TextSection, "content"> {
	content: string;
}

export interface ArticleDraftDto extends Omit<ArticleDraft, "description" | "content" | "banner" | "workspaceIds"> {
	description: string;
	content: ArticleContentDto;
	bannerId?: string;
	workspaceIds?: string[];
}

export function articleDraftToDto(draft: ArticleDraft): ArticleDraftDto {
	const { content, banner } = draft;
	return {
		...draft,
		description: JSON.stringify(convertToRaw(draft.description)),
		bannerId: banner?.id,
		content: {
			...content,
			sections: articleSectionsToDto(content.sections.filter((section) => section.type !== "LOADING")),
			assets: [],
			tags: content.tags.toArray(),
		},
		workspaceIds: draft.workspaceIds?.toArray(),
	};
}

export interface ArticleDto extends Omit<
	Article,
	"author" | "description" | "lastUpdateDate" | "publishedAt" | "workspaceIds" | "content"
> {
	author?: PersonDto;
	description: string;
	lastUpdateDate: string;
	publishedAt?: string;
	content: ArticleContentDto;
}

export function articleToDto(article: Article): ArticleDto {
	const { author, description, lastUpdateDate, publishedAt, content } = article;
	return {
		...article,
		author: author ? personToDto(author) : undefined,
		description: JSON.stringify(convertToRaw(description)),
		lastUpdateDate: lastUpdateDate.toISOString(),
		publishedAt: publishedAt ? publishedAt.toISOString() : undefined,
		content: {
			...content,
			sections: articleSectionsToDto(content.sections),
			assets: [],
		},
	};
}

export function articleFromDto(article: ArticleDto): Article {
	const { author, description, lastUpdateDate, publishedAt, content: initialContent } = article;
	let content: ArticleContent;
	try {
		content = {
			...initialContent,
			sections: List(initialContent.sections.map(articleSectionFromDto)),
			assets: initialContent.assets ? List(initialContent.assets.map((asset) => mediaFromDto(asset))) : List(),
		};
	} catch {
		content = {
			sections: List(),
			assets: List(),
			tags: [],
		};
	}
	return {
		...article,
		author: author ? personFromDto(author) : undefined,
		description: contentStateFromString(description),
		lastUpdateDate: new Date(lastUpdateDate),
		publishedAt: publishedAt ? new Date(publishedAt) : undefined,
		content,
		contentType: "ARTICLE",
	};
}

function articleSectionFromDto(dto: ArticleSectionDto): ArticleSection {
	if (dto.type === "TEXT") {
		return {
			...dto,
			content: contentStateFromString(dto.type),
		};
	}
	return dto;
}

function contentStateFromString(content: string): ContentState {
	try {
		return convertFromRaw(JSON.parse(content));
	} catch {
		return convertFromHTML({})(content);
	}
}

function articleSectionsToDto(sections: List<ArticleSection>): ArticleSectionDto[] {
	return sections
		.map((section) =>
			section.type === "TEXT" ? { ...section, content: JSON.stringify(convertToRaw(section.content)) } : section
		)
		.toArray();
}

export type LightArticle = Omit<Article, "content">;
export type DetailedArticle = Article & {
	workspaceIds?: string[];
};
